import type { ErpModule } from '@/api/platform.api';
import { getModuleLoginPath, MODULE_ENTRIES } from '@/config/moduleEntries';
import { resolveSettingsModuleKey } from '@/config/settingsRegistry';
import { MASTER_SIGN_IN_PATH, MODULE_HUB_PATH } from '@/constants/authRoutes';
import { isSettingsPath } from '@/utils/settingsBreadcrumbs';
import {
  readLastModuleSignIn,
  readStoredEntryModule,
  readStoredWorkspaceModule,
} from '@/utils/moduleScope';

export type ModuleSignInPathOptions = {
  pathname?: string;
  entryModule?: string | null;
  activeModule?: string | null;
  masterAdmin?: boolean;
};

/**
 * Infer module only from primary product routes / module settings paths.
 * Shared routes (/settings, /notifications, /org/*, …) must not map to POS
 * (or any first module) — those fall through to workspace/entry context.
 */
export function resolvePrimaryModuleFromPath(pathname: string): ErpModule | null {
  const path = pathname.split('?')[0] || '';

  if (isSettingsPath(path)) {
    const moduleKey = resolveSettingsModuleKey(path);
    if (moduleKey !== 'PLATFORM') {
      return moduleKey as ErpModule;
    }
    return null;
  }

  let best: { module: ErpModule; score: number } | null = null;
  for (const entry of MODULE_ENTRIES) {
    for (const prefix of entry.routePrefixes) {
      const matches =
        path === prefix || (prefix !== '/pos' && path.startsWith(`${prefix}/`));
      if (!matches) continue;
      const score = prefix.length;
      if (!best || score > best.score) {
        best = { module: entry.erpModule, score };
      }
    }
  }
  return best?.module ?? null;
}

/**
 * Resolve the product sign-in URL after logout or session expiry.
 *
 * Priority:
 * 1. Master admin → /master/login
 * 2. Unambiguous primary module path (e.g. /hr/employees → /hr/login)
 * 3. Active workspace module (what the user was working in)
 * 4. Entry module (scoped login)
 * 5. Stored workspace / entry / last sign-in module
 * 6. Module hub
 */
export function resolveModuleSignInPath(options: ModuleSignInPathOptions = {}): string {
  const pathname =
    options.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');

  if (options.masterAdmin || pathname.startsWith('/master')) {
    return MASTER_SIGN_IN_PATH;
  }

  const fromRoute = resolvePrimaryModuleFromPath(pathname);
  if (fromRoute) {
    return getModuleLoginPath(fromRoute);
  }

  const module = options.activeModule ?? options.entryModule;
  if (module) {
    return getModuleLoginPath(module);
  }

  const stored =
    readStoredWorkspaceModule() ?? readStoredEntryModule() ?? readLastModuleSignIn();
  if (stored) {
    return getModuleLoginPath(stored);
  }

  return MODULE_HUB_PATH;
}
